/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2024 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import getInvoicePaymentPaymetricResponsePacket from "@eisinc/App/Handlers/Components/GetInvoicePaymentPaymetricResponsePacket";
import loadInvoicePaymentPaymetricConfig from "@eisinc/App/Handlers/Components/LoadInvoicePaymentPaymetricConfig";
import loadInvoicePaymentOptions from "@eisinc/App/Handlers/LoadInvoicePaymentOptions";
import { xCheckoutActions, xCheckoutState } from "@eisinc/App/Store/Reducers";
import { generateTokenExFrameStyleConfig } from "@eisinc/MobiusOverrides/TokenExFrame";
import { color } from "@eisinc/XDL/Utilities";
import { usePaymetricFrame } from "@insite/client-framework/Common/Hooks/usePaymetricFrame";
import { usePaystandFrame } from "@insite/client-framework/Common/Hooks/usePaystandFrame";
import { useSquareFrame } from "@insite/client-framework/Common/Hooks/useSquareFrame";
import { useTokenExFrame } from "@insite/client-framework/Common/Hooks/useTokenExFrame";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import validateCreditCard from "@insite/client-framework/Common/Utilities/validateCreditCard";
import logger from "@insite/client-framework/Logger";
import { TokenExConfig } from "@insite/client-framework/Services/SettingsService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadPaymetricConfig from "@insite/client-framework/Store/Context/Handlers/LoadPaymetricConfig";
import loadTokenExConfig from "@insite/client-framework/Store/Context/Handlers/LoadTokenExConfig";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import getPaymetricResponsePacket from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/GetPaymetricResponsePacket";
import translate from "@insite/client-framework/Translate";
import { BaseAddressModel, PaymentMethodDto } from "@insite/client-framework/Types/ApiModels";
import CreditCardBillingAddressEntry, {
    CreditCardBillingAddressEntryStyles,
} from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CreditCardBillingAddressEntry";
import CreditCardDetailsEntry, {
    CreditCardDetailsEntryStyles,
} from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CreditCardDetailsEntry";
import { Validatable } from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/ECheckDetailsEntry";
import PaymentProfileBillingAddress from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/PaymentProfileBillingAddress";
import {
    convertApiDataToTokenExCardType,
    convertPaymetricCardType,
    convertTokenExCardType,
} from "@insite/content-library/Widgets/SavedPayments/PaymentUtilities";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import { GridContainer, GridItem, Link, LoadingSpinner, Typography } from "@insite/mobius/index";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";
import { css, ThemeProps, withTheme } from "styled-components";

export type TokenExIframeStyles = {
    base?: string;
    focus?: string;
    error?: string;
    cvv?: TokenExIframeStyles;
};

export type TokenExIframeConfig = {
    tokenExID: string;
    tokenScheme: string;
    authenticationKey: string;
    timestamp: string;
    origin: string;
    styles?: TokenExIframeStyles;
    pci?: boolean;
    enableValidateOnBlur?: boolean;
    inputType?: "number" | "tel" | "text";
    debug?: boolean;
    cvv?: boolean;
    cvvOnly?: boolean;
    cvvContainerID?: string;
    cvvInputType?: "number" | "tel" | "text";
    enableAriaRequired?: boolean;
    customDataLabel?: string;
    title?: string;
    placeholder?: string;
    cvvPlaceholder?: string;
};

type TokenExPCIIframeConfig = TokenExIframeConfig & {
    pci: true;
    enablePrettyFormat?: boolean;
};

type TokenExCvvOnlyIframeConfig = TokenExIframeConfig & {
    cvvOnly: true;
    token?: string;
    cardType?: string;
};

export type IFrame = {
    new (containerId: string, config: TokenExPCIIframeConfig | TokenExCvvOnlyIframeConfig): IFrame;
    load: () => void;
    on: (
        eventName: "load" | "validate" | "tokenize" | "error" | "cardTypeChange",
        callback: (data: any) => void,
    ) => void;
    remove: () => void;
    validate: () => void;
    tokenize: () => void;
    reset: () => void;
};

export type TokenEx = {
    Iframe: IFrame;
};

declare const TokenEx: TokenEx;
let tokenExIframe: IFrame | undefined;
let tokenExAccountNumberIframe: IFrame | undefined;

declare function $XIFrame(options: any): any;
let paymetricIframe: any;

let tokenExFrameStyleConfig: TokenExIframeStyles;
const isNonEmptyString = (value: string | undefined) => value !== undefined && value.trim() !== "";

const isMonthAndYearBeforeToday = (month: number, year: number) => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    return year < currentYear || (year === currentYear && month < currentMonth + 1);
};

const StyledForm = getStyledWrapper("form");
const StyledFieldSet = getStyledWrapper("fieldset");

interface OwnProps {
    setCurrentStep: (i: number) => void;
    onSubmit: (data: any) => void;
}

export interface InvoicePaymentDetailsStyle {
    wrapper?: InjectableCss;
    backLink?: LinkPresentationProps;
    paymentContainer?: GridContainerProps;
    leftGridItem?: GridItemProps;
    rightGridItem?: GridItemProps;
    creditCardDetails?: CreditCardDetailsEntryStyles;
    creditCardAddress?: CreditCardBillingAddressEntryStyles;
    loadingSpinnerWrapper?: InjectableCss;
    paymentMethodSelectWrapper?: InjectableCss;
}

export const styles: InvoicePaymentDetailsStyle = {
    wrapper: {
        css: css`
            width: 100%;
        `,
    },
    backLink: {
        icon: {
            iconProps: {
                size: 18,
                src: ChevronLeft,
            },
        },
        css: css`
            margin-left: -5px;
            margin-bottom: 18px;

            ${IconWrapper} {
                margin-right: 4px;
            }
        `,
    },
    paymentContainer: {
        gap: 30,
    },
    leftGridItem: {
        width: [12, 12, 6, 5, 5],
        css: css`
            flex-direction: column;
        `,
    },
    rightGridItem: {
        width: [12, 12, 6, 5, 5],
        css: css`
            flex-direction: column;
        `,
    },
    creditCardDetails: {
        paymetricIframe: {
            css: css`
                border: 0;
                width: 100%;
            `,
        },
    },
    loadingSpinnerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            margin: 15px 0;
            width: 100%;
            color: ${color("primary.main")};
        `,
    },
    paymentMethodSelectWrapper: {
        css: css`
            margin-bottom: 24px;
            width: 100%;
        `,
    },
};

const mapStateToProps = (state: ApplicationState & xCheckoutState) => {
    const settingsCollection = getSettingsCollection(state);
    const cartState = getCurrentCartState(state);

    return {
        countries: getCurrentCountries(state),
        userRoles: state.context.session?.userRoles,
        savedPaymentsPageLink: getPageLinkByPageType(state, "SavedPaymentsPage"),
        bypassCvvForSavedCards: settingsCollection.cartSettings.bypassCvvForSavedCards,
        websiteSettings: settingsCollection.websiteSettings,
        tokenExConfigs: state.context.tokenExConfigs,
        paymetricConfig: state.context.paymetricConfig,
        cardAddress2: state.xCheckout.cardAddress2,
        billToState: getBillToState(state, cartState.value ? cartState.value.billToId : undefined),
    };
};

const mapDispatchToProps = {
    loadInvoicePaymentOptions,
    loadTokenExConfig,
    loadPaymetricConfig,
    loadInvoicePaymentPaymetricConfig,
    getPaymetricResponsePacket,
    getInvoicePaymentPaymetricResponsePacket,
    loadCurrentCountries,
    setPaymentProfile: (paymentProfile: string) => (dispatch: Dispatch<xCheckoutActions>) => {
        dispatch({ type: "xCheckout/setPaymentProfile", paymentProfile });
    },
    setExpDate: (expDate: string) => (dispatch: Dispatch<xCheckoutActions>) => {
        dispatch({ type: "xCheckout/setExpDate", expDate });
    },
    setLastFour: (lastFour: string) => (dispatch: Dispatch<xCheckoutActions>) => {
        dispatch({ type: "xCheckout/setLastFour", lastFour });
    },
    setCcType: (ccType: string) => (dispatch: Dispatch<xCheckoutActions>) => {
        dispatch({ type: "xCheckout/setCcType", ccType });
    },
    setPo: (po: string) => (dispatch: Dispatch<xCheckoutActions>) => {
        dispatch({ type: "xCheckout/setPo", po });
    },
    setPaymentMethodX: (paymentMethod: string) => (dispatch: Dispatch<xCheckoutActions>) => {
        dispatch({ type: "xCheckout/setPaymentMethod", paymentMethod });
    },
    setCardNickName: (cardNickName: string) => (dispatch: Dispatch<xCheckoutActions>) => {
        dispatch({ type: "xCheckout/SetCardNickName", cardNickName });
    },
    setCardAddress2: (cardAddress2: string) => (dispatch: Dispatch<xCheckoutActions>) => {
        dispatch({ type: "xCheckout/setCardAddress2", cardAddress2 });
    },
};

type Props = OwnProps &
    ResolveThunks<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

const InvoicePaymentDetails: React.FunctionComponent<Props> = ({
    countries,
    userRoles,
    savedPaymentsPageLink,
    bypassCvvForSavedCards,
    websiteSettings,
    tokenExConfigs,
    paymetricConfig,
    theme,
    history,
    setCurrentStep,
    onSubmit,
    loadInvoicePaymentOptions,
    loadTokenExConfig,
    loadPaymetricConfig,
    loadInvoicePaymentPaymetricConfig,
    setPaymentProfile,
    setExpDate,
    setLastFour,
    setCcType,
    setPo,
    setPaymentMethodX,
    setCardNickName,
    cardAddress2,
    setCardAddress2,
    billToState,
    loadCurrentCountries,
    getPaymetricResponsePacket,
    getInvoicePaymentPaymetricResponsePacket,
}: Props) => {
    const [showSpinner, setShowSpinner] = useState(false);
    const [paymentOptions, setPaymentOptions] = useState({
        paymentMethods: [],
        isPayPal: false,
        cardTypes: [],
        canStorePaymentProfile: false,
        expirationMonths: [],
        expirationYears: [],
    });
    const [paymentMethods, setPaymentMethods] = useState([] as PaymentMethodDto[]);
    const [paymentMethod, setPaymentMethod] = useState("");
    const [isIframeLoaded, setIsIframeLoaded] = useState(false);

    const [showFormErrors, setShowFormErrors] = useState(false);
    const [paymetricValidateSuccess, setPaymetricValidateSuccess] = useState(false);
    const [isCardNumberTokenized, setIsCardNumberTokenized] = useState(false);
    const [isTokenExIframeLoaded, setIsTokenExIframeLoaded] = useState(false);
    const [saveCard, setSaveCard] = useState(false);
    const [isPaymetricSuccess, setIsPaymetricSuccess] = useState(false);
    const [expirationMonth, setExpirationMonth] = useState(1);
    const [expirationYear, setExpirationYear] = useState(new Date().getFullYear());

    const [accountNumber, setAccountNumber] = useState("");
    const [accountType, setAccountType] = useState("");
    const [bankName, setBankName] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [nameOnAccount, setNameOnAccount] = useState("");
    const [routingNumber, setRoutingNumber] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");

    const [possibleCardType, setPossibleCardType] = useState("");
    const [securityCode, setSecurityCode] = useState("");
    const [useBillingAddress, setUseBillingAddress] = useState(false);
    const [paymentMethodError, setPaymentMethodError] = useState("");
    const [securityCodeError, setSecurityCodeError] = useState("");
    const [cardNumberError, setCardNumberError] = useState("");
    const [cardNumber, setCardNumber] = useState("");
    const [cardType, setCardType] = useState("");
    const [cardTypeError, setCardTypeError] = useState("");
    const [cardHolderName, setCardHolderName] = useState("");
    const [cardHolderNameError, setCardHolderNameError] = useState("");
    const [expirationError, setExpirationError] = useState("");

    const [address1, setAddress1] = useState("");
    const [countryId, setCountryId] = useState("");
    const [stateId, setStateId] = useState("");
    const [city, setCity] = useState("");
    const [postalCode, setPostalCode] = useState("");

    const [paystandError, setPaystandError] = useState("");

    const [address1Error, setAddress1Error] = useState("");
    const [countryError, setCountryError] = useState("");
    const [stateError, setStateError] = useState("");
    const [cityError, setCityError] = useState("");
    const [postalCodeError, setPostalCodeError] = useState("");

    const isBuyer1 = (userRoles || "").includes("Buyer1");

    // Used in deciding which form element to focus on in the case the form is submitted with errors
    const paymentMethodRef = useRef<HTMLSelectElement>(null);
    const cardHolderNameRef = useRef<HTMLInputElement>(null);
    const cardNumberRef = useRef<HTMLInputElement>(null);
    const cardTypeRef = useRef<HTMLSelectElement>(null);
    const securityCodeRef = useRef<HTMLInputElement>(null);
    const expirationMonthRef = useRef<HTMLSelectElement>(null);
    const expirationYearRef = useRef<HTMLSelectElement>(null);
    const address1Ref = useRef<HTMLInputElement>(null);
    const countryRef = useRef<HTMLSelectElement>(null);
    const stateRef = useRef<HTMLSelectElement>(null);
    const cityRef = useRef<HTMLInputElement>(null);
    const postalCodeRef = useRef<HTMLInputElement>(null);
    const [isPayPal, setIsPayPal] = useState(false);

    const resetForm = () => {
        setCardHolderNameError("");
        setCardNumberError("");
        setCardTypeError("");
        setExpirationError("");
        setSecurityCodeError("");
        setAddress1("");
        setCountryError("");
        setStateError("");
        setCityError("");
        setPostalCodeError("");
        setPaystandError("");
        setIsCardNumberTokenized(false);
        setIsTokenExIframeLoaded(false);
        // customization
        setIsPaymetricSuccess(false);
        setPaymentProfile("");
        setExpDate("");
        setLastFour("");
        setCcType("");
    };

    useTokenExFrame();

    const {
        useTokenExGateway,
        useECheckTokenExGateway,
        usePaymetricGateway,
        useSquareGateway,
        useAdyenDropIn,
        usePaystandDropIn,
    } = websiteSettings;

    const paymentMethodDto = paymentMethods?.find(method => method.name === paymentMethod) as any;
    // Show the iframe by default, there is no payment method selector
    const isCreditCard = paymentMethodDto?.isCreditCard === true || true;
    const isECheck = paymentMethodDto?.isECheck === true;
    const isPaymentProfile = paymentMethodDto?.isPaymentProfile === true;
    const isPaymentProfileExpired = paymentMethodDto?.isPaymentProfileExpired === true;
    const selectedCountry = countries?.find(country => country.id === countryId);
    let tokenName: string | undefined;
    const eCheckDetails = useRef<Validatable>(null);
    const tokenExReinitTimeout = 500;
    let lastTokenExReinitTime = 0;
    const tokenExErrorsLimit = (20 * 1e3) / tokenExReinitTimeout;
    let currentTokenExErrorCount = 0;

    if (useTokenExGateway) {
        if (isPaymentProfile) {
            tokenName = paymentMethodDto!.name;
        } else if (isCreditCard) {
            tokenName = "";
        }
    }

    if (useECheckTokenExGateway) {
        if (isECheck) {
            tokenName = "";
        }
    }

    const tokenExConfig = typeof tokenName !== "undefined" ? tokenExConfigs[tokenName] : undefined;

    const setUpTokenEx = () => {
        if (!tokenExConfig || !paymentMethodDto) {
            return;
        }

        currentTokenExErrorCount = 0;
        if (isPaymentProfile && !isPaymentProfileExpired && !bypassCvvForSavedCards) {
            setUpTokenExIFrameCvvOnly(tokenExConfig);
        } else if (isCreditCard) {
            setUpTokenExIFrame(tokenExConfig);
        }
    };

    const setUpTokenExIFrame = (config: TokenExConfig) => {
        if (tokenExIframe) {
            tokenExIframe.remove();
        }

        const iframeConfig: TokenExPCIIframeConfig = {
            authenticationKey: config.authenticationKey,
            cvv: true,
            cvvContainerID: "tokenExSecurityCode",
            cvvInputType: "text",
            enablePrettyFormat: true,
            inputType: "text",
            origin: config.origin,
            pci: true,
            styles: tokenExFrameStyleConfig,
            timestamp: config.timestamp,
            tokenExID: config.tokenExId,
            tokenScheme: config.tokenScheme,
            enableAriaRequired: true,
            title: translate("Credit card information"),
            customDataLabel: translate("credit card number"),
        };

        tokenExIframe = new TokenEx.Iframe("tokenExCardNumber", iframeConfig);
        tokenExIframe.load();
        tokenExIframe.on("load", _ => setIsTokenExIframeLoaded(true));
        tokenExIframe.on("tokenize", data => {
            setCardNumber(data.token);
            setSecurityCode("CVV");
            setCardType(convertTokenExCardType(data.cardType));
            setIsCardNumberTokenized(true);
        });
        tokenExIframe.on("cardTypeChange", data => setPossibleCardType(data.possibleCardType));
        tokenExIframe.on("validate", data => {
            setShowFormErrors(true);
            if (data.isValid) {
                setCardNumberError("");
                data.lastFour && setLastFour(data.lastFour);
            } else {
                if (data.validator === "required") {
                    setCardNumberError(translate("Credit card number is required."));
                }
                if (data.validator === "format") {
                    setCardNumberError(translate("Credit card number is invalid."));
                }
            }

            if (data.isCvvValid) {
                setSecurityCodeError("");
            } else {
                if (data.cvvValidator === "required") {
                    setSecurityCodeError(translate("Security code is required."));
                }
                if (data.cvvValidator === "format") {
                    setSecurityCodeError(translate("Security code is invalid."));
                }
            }

            if (data.cardType) {
                const map: any = {
                    visa: "VISA",
                    americanExpress: "AMERICAN EXPRESS",
                    diners: "DINERS",
                    discover: "DISCOVER",
                    jcb: "JCB",
                    masterCard: "MASTERCARD",
                };
                const val =
                    data.cardType &&
                    (paymentOptions?.cardTypes as any).find((i: any) => i.value === map[data.cardType]);
                setCardType(val?.value);
                setCcType(map[data.cardType]);
                validateCardType(val?.value);
            } else {
                setCardType("");
                setCcType("");
                validateCardType("");
            }
        });
        tokenExIframe.on("error", data => {
            logger.error(data);
            if (
                Date.now() - lastTokenExReinitTime > tokenExReinitTimeout &&
                ++currentTokenExErrorCount <= tokenExErrorsLimit
            ) {
                lastTokenExReinitTime = Date.now();
                setTimeout(() => {
                    setUpTokenExIFrame(config);
                }, tokenExReinitTimeout);
            }
        });
    };

    const setUpTokenExIFrameCvvOnly = (config: TokenExConfig) => {
        if (paymentMethodDto?.isPaymentProfileExpired) {
            return;
        }
        if (tokenExIframe) {
            tokenExIframe.remove();
        }

        const iframeConfig: TokenExCvvOnlyIframeConfig = {
            authenticationKey: config.authenticationKey,
            cardType: convertApiDataToTokenExCardType(paymentMethodDto!.cardType),
            cvv: true,
            cvvOnly: true,
            inputType: "text",
            origin: config.origin,
            styles: tokenExFrameStyleConfig,
            timestamp: config.timestamp,
            token: paymentMethodDto!.name,
            tokenExID: config.tokenExId,
            tokenScheme: paymentMethodDto!.tokenScheme,
            enableAriaRequired: true,
            title: translate("Security code"),
            customDataLabel: translate("Security code"),
        };

        tokenExIframe = new TokenEx.Iframe("ppTokenExSecurityCode", iframeConfig);
        tokenExIframe.load();
        tokenExIframe.on("load", _ => setIsTokenExIframeLoaded(true));
        tokenExIframe.on("tokenize", _ => setIsCardNumberTokenized(true));
        tokenExIframe.on("validate", data => {
            if (data.isValid) {
                setSecurityCodeError("");
            } else {
                setShowFormErrors(true);
                if (data.validator === "required") {
                    setSecurityCodeError(translate("Security code is required."));
                }
                if (data.validator === "format") {
                    setSecurityCodeError(translate("Security code is invalid."));
                }
            }
        });
        tokenExIframe.on("error", data => {
            logger.error(data);
            if (
                Date.now() - lastTokenExReinitTime > tokenExReinitTimeout &&
                ++currentTokenExErrorCount <= tokenExErrorsLimit
            ) {
                lastTokenExReinitTime = Date.now();
                setTimeout(() => {
                    setUpTokenExIFrameCvvOnly(config);
                }, tokenExReinitTimeout);
            }
        });
    };

    const [paymetricFrameRef, paymetricIframe] = usePaymetricFrame(
        usePaymetricGateway,
        paymentMethod,
        isCreditCard,
        paymetricConfig,
        loadInvoicePaymentPaymetricConfig,
        // loadPaymetricConfig
    );

    const [paystandCard] = usePaystandFrame(websiteSettings, paymentMethod, isCreditCard);
    const [squareCard] = useSquareFrame(websiteSettings, paymentMethod, isCreditCard);

    const tokenizeSquareCard = () => {
        squareCard?.tokenize().then((tokenResult: any) => {
            if (tokenResult.status === "OK") {
                setCardType(tokenResult.details.card.brand);
                setExpirationMonth(tokenResult.details.card.expMonth);
                setExpirationYear(tokenResult.details.card.expYear);
                setCardNumber(tokenResult.token);
                setIsCardNumberTokenized(true);
            }
        });
    };

    const detectCardType = (cardNumber: string) => {
        const re: any = {
            VISA: /^4[0-9]{12}(?:[0-9]{3})?$/,
            MASTERCARD: /^5[1-5][0-9]{14}$/,
            "AMERICAN EXPRESS": /^3[47][0-9]{13}$/,
            DISCOVER: /^6(?:011|5[0-9]{2})[0-9]{12}$/,
            // electron: /^(4026|417500|4405|4508|4844|4913|4917)\d+$/,
            // maestro: /^(5018|5020|5038|5612|5893|6304|6759|6761|6762|6763|0604|6390)\d+$/,
            // dankort: /^(5019)\d+$/,
            // interpayment: /^(636)\d+$/,
            // unionpay: /^(62|88)\d+$/,
            // diners: /^3(?:0[0-5]|[68][0-9])[0-9]{11}$/,
            // jcb: /^(?:2131|1800|35\d{3})\d{11}$/,
        };
        for (const key in re) {
            if (re[key].test(cardNumber)) {
                return key;
            }
        }
    };

    const handlePaymentMethodChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (event.currentTarget.value !== "PayPal") {
            setIsPayPal(false);
        }
        setPaymentMethod(event.currentTarget.value);
        validatePaymentMethod(event.currentTarget.value);

        // setCheckoutPaymentMethod({ paymentMethod: paymentMethods?.find(method => method.name === event.target.value) });
    };

    const handleSaveCardChange = (_: React.SyntheticEvent<Element, Event>, value: boolean) => setSaveCard(value);

    const handleCardHolderNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCardHolderName(event.currentTarget.value);
        validateCardHolderName(event.currentTarget.value);
    };

    const onCardNickNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCardNickName(event.currentTarget.value);
    };

    const setCardAddress2Change = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCardAddress2(event.currentTarget.value);
    };

    const handleCardNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCardNumber(event.currentTarget.value);
        validateCardNumber(event.currentTarget.value);
        if (event.currentTarget.value.length >= 13) {
            const type = detectCardType(event.currentTarget.value);
            if (type && paymentOptions?.cardTypes?.find((item: any) => item.value === type)) {
                setCardType(type);
                validateCardType(type);
            }
        }
    };

    const handleCardTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setCardType(event.currentTarget.value);
        validateCardType(event.currentTarget.value);
    };

    const handleExpirationMonthChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const month = Number(event.currentTarget.value);
        if (Number.isNaN(month)) {
            return;
        }
        setExpirationMonth(month);
        validateCardExpiration(month, expirationYear);
    };

    const handleExpirationYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const year = Number(event.currentTarget.value);
        if (Number.isNaN(year)) {
            return;
        }
        setExpirationYear(year);
        validateCardExpiration(expirationMonth, year);
    };

    const handleSecurityCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSecurityCode(event.currentTarget.value);
        validateSecurityCode(event.currentTarget.value);
    };

    const handleUseBillingAddressChange = (_: React.SyntheticEvent<Element, Event>, value: boolean) =>
        setUseBillingAddress(value);

    const handleAddressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setAddress1(event.currentTarget.value);
        validateAddress1(event.currentTarget.value);
    };

    const handleCountryChange = (event: React.ChangeEvent<HTMLSelectElement>, country: BaseAddressModel["country"]) => {
        setCountryId(country?.id || "");
        validateCountry(country?.id || "");
        setStateId("");
    };

    const handleStateChange = (event: React.ChangeEvent<HTMLSelectElement>, state: BaseAddressModel["state"]) => {
        setStateId(state?.id || "");
        validateState(state?.id || "");
    };

    const handleCityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCity(event.currentTarget.value);
        validateCity(event.currentTarget.value);
    };

    const handlePostalCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPostalCode(event.currentTarget.value);
        validatePostalCode(event.currentTarget.value);
    };

    const handleEditCardClick = () => {
        if (!savedPaymentsPageLink) {
            return;
        }

        history.push(savedPaymentsPageLink.url);
    };

    const validatePaymentMethod = (paymentMethod: string) => {
        const paymentMethodValid =
            isPayPal || !paymentMethods || paymentMethods.length === 0 || isNonEmptyString(paymentMethod);
        setPaymentMethodError(!paymentMethodValid ? translate("Payment Method is required.") : "");
        return paymentMethodValid;
    };

    const validateCardHolderName = (cardHolderName: string) => {
        const cardHolderNameValid = !isCreditCard || isNonEmptyString(cardHolderName) || usePaymetricGateway;
        setCardHolderNameError(!cardHolderNameValid ? translate("Cardholder name is required.") : "");
        return cardHolderNameValid;
    };

    const validateCardNumber = (cardNumber: string) => {
        let cardNumberEmpty = false;
        let cardNumberValid = true;

        if (isCreditCard && !useTokenExGateway && !usePaymetricGateway) {
            cardNumberEmpty = !isNonEmptyString(cardNumber);
            cardNumberValid = validateCreditCard(cardNumber);
        }

        if (cardNumberEmpty) {
            setCardNumberError(translate("Credit card number is required."));
        } else if (!cardNumberValid) {
            setCardNumberError(translate("Credit card number is invalid."));
        } else {
            setCardNumberError("");
        }

        return { cardNumberEmpty, cardNumberValid };
    };

    const validateCardType = (cardType: string) => {
        const cardTypeValid =
            !isCreditCard ||
            useTokenExGateway ||
            (!useTokenExGateway && isNonEmptyString(cardType)) ||
            usePaymetricGateway;
        setCardTypeError(!cardTypeValid ? translate("Credit card type is required.") : "");
        return cardTypeValid;
    };

    const validateCardExpiration = (expirationMonth: number, expirationYear: number) => {
        let cardExpired = isCreditCard && isMonthAndYearBeforeToday(expirationMonth, expirationYear);
        if (isCreditCard && usePaymetricGateway) {
            cardExpired = false;
        }
        setExpirationError(cardExpired ? translate("Card is expired. Please enter a valid expiration date.") : "");
        return cardExpired;
    };

    const validateSecurityCode = (securityCode: string) => {
        let securityCodeEmpty = false;
        let securityCodeValid = true;

        if (isCreditCard && !useTokenExGateway && !usePaymetricGateway) {
            securityCodeEmpty = !isNonEmptyString(securityCode);
            securityCodeValid = /^\d+$/.test(securityCode);
        }

        if (securityCodeEmpty) {
            setSecurityCodeError(!securityCodeValid ? translate("Security code is required.") : "");
        } else if (!securityCodeValid) {
            setSecurityCodeError(translate("Security code is invalid."));
        } else {
            setSecurityCodeError("");
        }

        return { securityCodeEmpty, securityCodeValid };
    };

    const validateAddress1 = (address1: string) => {
        const address1Valid =
            !(isCreditCard || isECheck) || useBillingAddress || (!useBillingAddress && isNonEmptyString(address1));
        setAddress1Error(!address1Valid ? translate("Address is required.") : "");
        return address1Valid;
    };

    const validateCountry = (countryId: string) => {
        const countryValid =
            !(isCreditCard || isECheck) || useBillingAddress || (!useBillingAddress && isNonEmptyString(countryId));
        setCountryError(!countryValid ? translate("Country is required.") : "");
        return countryValid;
    };

    const validateState = (stateId: string) => {
        const stateValid =
            (!!selectedCountry && !!selectedCountry.states && selectedCountry.states.length <= 0) ||
            !(isCreditCard || isECheck) ||
            useBillingAddress ||
            (!useBillingAddress && isNonEmptyString(stateId));
        setStateError(!stateValid ? translate("State is required.") : "");
        return stateValid;
    };

    const validateCity = (city: string) => {
        const cityValid =
            !(isCreditCard || isECheck) || useBillingAddress || (!useBillingAddress && isNonEmptyString(city));
        setCityError(!cityValid ? translate("City is required.") : "");
        return cityValid;
    };

    const validatePostalCode = (postalCode: string) => {
        const postalCodeValid =
            !(isCreditCard || isECheck) || useBillingAddress || (!useBillingAddress && isNonEmptyString(postalCode));
        setPostalCodeError(!postalCodeValid ? translate("Postal Code is required.") : "");
        return postalCodeValid;
    };

    const handlePaymetricValidateSuccess = (success: boolean) => {
        setIsPaymetricSuccess(success);

        if (success) {
            paymetricIframe.submit({
                onSuccess: (msg: string) => {
                    // The HasPassed is case sensitive, and not standard json.
                    const message: { data: { HasPassed: boolean } } = JSON.parse(msg);
                    if (message.data.HasPassed) {
                        handleSuccessSubmitPaymetricIframe();
                    }
                },
                onError: (msg: string) => {
                    const message: { data: { Code: number } } = JSON.parse(msg);
                    // Code = 150 -> Already submitted
                    if (message.data.Code === 150) {
                        handleSuccessSubmitPaymetricIframe();
                    }
                },
            });
        }
    };

    const handleSuccessSubmitPaymetricIframe = () => {
        if (!paymetricConfig?.accessToken) {
            return;
        }

        getInvoicePaymentPaymetricResponsePacket({
            accessToken: paymetricConfig.accessToken,
            onComplete: result => {
                if (result.apiResult?.success) {
                    if (result.apiResult.creditCard?.cardNumber) {
                        setCardType(convertPaymetricCardType(result.apiResult.creditCard.cardType));
                        setExpirationMonth(result.apiResult.creditCard.expirationMonth!);
                        setExpirationYear(result.apiResult.creditCard.expirationYear!);
                        setCardNumber(result.apiResult.creditCard.cardNumber!);
                        setSecurityCode(result.apiResult.creditCard.securityCode!);
                        setCardHolderName(result.apiResult.creditCard.cardHolderName!);
                    } else {
                        setCardType("EC");
                        setAccountNumber((result.apiResult as any)?.eCheck.accountNumber);
                        setAccountType((result.apiResult as any)?.eCheck.accountType);
                        setBankName((result.apiResult as any)?.eCheck.bankName);
                        setFirstName((result.apiResult as any)?.eCheck.firstName);
                        setLastName((result.apiResult as any)?.eCheck.lastName);
                        setNameOnAccount((result.apiResult as any)?.eCheck.nameOnAccount);
                        setRoutingNumber((result.apiResult as any)?.eCheck.routingNumber);
                        setPhoneNumber((result.apiResult as any)?.eCheck.phoneNumber);
                    }

                    setIsCardNumberTokenized(true);
                }
            },
        });
    };

    const validateForm = () => {
        const paymentMethodValid = validatePaymentMethod(paymentMethod);

        if (isPayPal) {
            return paymentMethodValid;
        }

        // customization
        setPaymentMethodX(paymentMethod);
        isPaymentProfile && setPaymentProfile(paymentMethodDto?.description || "");

        if (useTokenExGateway && ((isPaymentProfile && !bypassCvvForSavedCards) || isCreditCard)) {
            tokenExIframe?.validate();
        } else if (useECheckTokenExGateway && isECheck) {
            tokenExAccountNumberIframe?.validate();
        }

        if (usePaymetricGateway) {
            const isFormValidForPaymetricPayment = paymentMethodValid;
            if (!isFormValidForPaymetricPayment) {
                return false;
            }
            if (isCreditCard && paymetricIframe) {
                paymetricIframe.validate({
                    onValidate: (success: boolean) => {
                        setPaymetricValidateSuccess(success);
                    },
                });
            } else if (isPaymentProfile) {
                return true;
            }
        }

        if (useSquareGateway) {
            const isFormValidForSquarePayment = paymentMethodValid;
            if (!isFormValidForSquarePayment) {
                return false;
            }
            if (isCreditCard && squareCard) {
                tokenizeSquareCard();
            } else if (isPaymentProfile) {
                return true;
            }
        }

        if (usePaystandDropIn && isCreditCard) {
            const isTokenExist = cardNumber;
            setPaystandError(!isTokenExist ? translate("Payment details are required.") : "");
            return isTokenExist;
        }

        const cardHolderNameValid = validateCardHolderName(cardHolderName);
        const cardNumberResult = validateCardNumber(cardNumber);
        const cardTypeValid = validateCardType(cardType);
        const cardExpired = validateCardExpiration(expirationMonth, expirationYear);
        const securityCodeResult = validateSecurityCode(securityCode);
        const address1Valid = validateAddress1(address1);
        const countryValid = validateCountry(countryId);
        const stateValid = validateState(stateId);
        const cityValid = validateCity(city);
        const postalCodeValid = validatePostalCode(postalCode);
        const accountHolderNameValid = true;
        const accountNumberValid = true;
        const routingNumberValid = true;
        // if (eCheckDetails?.current) {
        //     accountHolderNameValid = eCheckDetails.current.validateAccountHolderNameChange(accountHolderName);
        //     accountNumberValid = eCheckDetails.current.validateAccountNumberChange(accountNumber);
        //     routingNumberValid = eCheckDetails.current.validateRoutingNumberChange(routingNumber);
        // }

        if (!paymentMethodValid) {
            paymentMethodRef.current?.focus();
        } else if (!cardHolderNameValid) {
            cardHolderNameRef.current?.focus();
        } else if (cardNumberResult.cardNumberEmpty || !cardNumberResult.cardNumberValid) {
            cardNumberRef.current?.focus();
        } else if (!cardTypeValid) {
            cardTypeRef.current?.focus();
        } else if (cardExpired) {
            const today = new Date();
            if (expirationYear < today.getFullYear()) {
                expirationYearRef.current?.focus();
            } else {
                expirationMonthRef.current?.focus();
            }
        } else if (securityCodeResult.securityCodeEmpty || !securityCodeResult.securityCodeValid) {
            securityCodeRef.current?.focus();
        } else if (!address1Valid) {
            address1Ref.current?.focus();
        } else if (!countryValid) {
            countryRef.current?.focus();
        } else if (!stateValid) {
            stateRef.current?.focus();
        } else if (!cityValid) {
            cityRef.current?.focus();
        } else if (!postalCodeValid) {
            postalCodeRef.current?.focus();
        }

        // customization
        if (!useTokenExGateway && (paymentMethodDto?.isPaymentProfile || paymentMethodDto?.isCreditCard)) {
            setLastFour(cardNumber.substr(-4));
            setCcType(cardType);
        }
        if (paymentMethodDto?.isCreditCard) {
            setExpDate(`${expirationMonth}/${expirationYear}`);
        }

        return (
            paymentMethodValid &&
            cardHolderNameValid &&
            !cardNumberResult.cardNumberEmpty &&
            cardNumberResult.cardNumberValid &&
            cardTypeValid &&
            !cardExpired &&
            !securityCodeResult.securityCodeEmpty &&
            securityCodeResult.securityCodeValid &&
            address1Valid &&
            countryValid &&
            stateValid &&
            cityValid &&
            postalCodeValid &&
            accountHolderNameValid &&
            accountNumberValid &&
            routingNumberValid
        );
    };

    if (!tokenExFrameStyleConfig) {
        tokenExFrameStyleConfig = generateTokenExFrameStyleConfig({ theme });
    }

    const isPo = () => !paymentMethodDto?.isCreditCard && !paymentMethodDto?.isPaymentProfile;

    const isTokenExValidator = () =>
        useTokenExGateway && (paymentMethodDto?.isPaymentProfile || paymentMethodDto?.isCreditCard);

    const isTokenExHasError = () => !!cardNumberError || !!securityCodeError;

    const isPaymetricValidator = () => usePaymetricGateway && paymentMethodDto?.isCreditCard && paymetricIframe;

    const isPaymetricHasError = () => !isPaymetricSuccess;

    const isPaymetricProfile = () => usePaymetricGateway && paymentMethodDto?.isPaymentProfile;

    const isPaymetricProfileHasErrors = () => paymentMethodDto?.isPaymentProfileExpired;

    const validateFormRef = useRef(validateForm);
    validateFormRef.current = validateForm;

    // isPO
    const isPoRef = useRef(isPo);
    isPoRef.current = isPo;

    // tokenEx
    const isTokenExValidatorRef = useRef(isTokenExValidator);
    isTokenExValidatorRef.current = isTokenExValidator;
    const isTokenExHasErrorRef = useRef(isTokenExHasError);
    isTokenExHasErrorRef.current = isTokenExHasError;

    // Paymetric
    const isPaymetricValidatorRef = useRef(isPaymetricValidator);
    isPaymetricValidatorRef.current = isPaymetricValidator;
    const isPaymetricHasErrorRef = useRef(isPaymetricHasError);
    isPaymetricHasErrorRef.current = isPaymetricHasError;

    const isPaymetricProfileRef = useRef(isPaymetricProfile);
    isPaymetricProfileRef.current = isPaymetricProfile;
    const isPaymetricProfileHasErrorsRef = useRef(isPaymetricProfileHasErrors);
    isPaymetricProfileHasErrorsRef.current = isPaymetricProfileHasErrors;

    const iframeName = useTokenExGateway
        ? "TokenEx"
        : usePaymetricGateway
          ? "Paymetric"
          : useSquareGateway
            ? "Square"
            : usePaystandDropIn
              ? "Paystand"
              : undefined;

    const handleFormSubmit = (event: any) => {
        event?.preventDefault();
        const validForm = validateFormRef.current();

        if (!validForm) {
            setShowFormErrors(true);
            return false;
        }

        return new Promise(resolve => {
            setTimeout(() => {
                const isPo = isPoRef.current();
                const isTokenEx = isTokenExValidatorRef.current();
                const isPaymetric = isPaymetricValidatorRef.current();
                const isValid =
                    (validForm && isPo) ||
                    (validForm && isTokenEx && !isTokenExHasErrorRef.current()) ||
                    (validForm && isPaymetric && !isPaymetricHasErrorRef.current()) ||
                    (isPaymetricProfileRef.current() && !isPaymetricProfileHasErrorsRef.current());
                setShowFormErrors(!isValid);

                resolve(!isValid);
            }, 200);
        });
    };

    const formatPayload = (data: any) => {
        const payload = {} as any;

        let creditCard = {};
        let eCheck = {};

        if (useBillingAddress) {
            const billTo = billToState.value;
            creditCard = {
                cardType: data.cardType,
                cardHolderName: data.cardHolderName,
                cardNumber: data.cardNumber,
                expirationMonth: data.expirationMonth,
                expirationYear: data.expirationYear,
                securityCode: data.securityCode,
                useBillingAddress,
                storePaymentProfile: saveCard,
                address1: billTo?.address1,
                city: billTo?.city,
                state: billTo?.state?.name,
                stateAbbreviation: billTo?.state?.abbreviation,
                country: billTo?.country?.name,
                countryAbbreviation: billTo?.country?.abbreviation,
                postalCode: billTo?.postalCode,
            };
        } else {
            const selectedState = selectedCountry?.states?.find(state => state.id === stateId);

            if (data.cardType !== "EC") {
                creditCard = {
                    cardType: data.cardType,
                    cardHolderName: data.cardHolderName,
                    cardNumber: data.cardNumber,
                    expirationMonth: data.expirationMonth,
                    expirationYear: data.expirationYear,
                    securityCode: data.securityCode,
                    useBillingAddress,
                    storePaymentProfile: saveCard,
                    address1: data.address1,
                    city: data.city,
                    state: selectedState?.name,
                    stateAbbreviation: selectedState?.abbreviation,
                    country: selectedCountry?.name,
                    countryAbbreviation: selectedCountry?.abbreviation,
                    postalCode: data.postalCode,
                };

                if (cardAddress2.length > 0) {
                    (creditCard as any).creditCardAddress2 = cardAddress2;
                }
            } else if (data.cardType === "EC") {
                eCheck = {
                    cardType: data.cardType,
                    cardNumber: data.accountNumber,
                    buyerFirstName: data.firstName,
                    buyerLastName: data.lastName,
                    accountType: data.accountType,
                    routingNumber: data.routingNumber,
                    nameOnAccount: data.nameOnAccount,
                    phoneNumber: data.phoneNumber,

                    address1: data.address1,
                    city: data.city,
                    state: selectedState?.name,
                    stateAbbreviation: selectedState?.abbreviation,
                    country: selectedCountry?.name,
                    countryAbbreviation: selectedCountry?.abbreviation,
                    postalCode: data.postalCode,

                    // buyerCompany: "undefined",
                    // bankName: data.bankName,
                    // routingNumber: data.routingNumber,
                };
            }
        }

        payload.creditCard = creditCard;
        payload.eCheck = eCheck;

        return payload;
    };

    useEffect(() => {
        if (!countries) {
            loadCurrentCountries();
        }
    }, []);

    useEffect(() => {
        setPaymentMethods(paymentOptions?.paymentMethods);
        setIsPayPal(paymentOptions?.isPayPal);
    }, [paymentOptions]);

    useEffect(() => {
        if (typeof TokenEx === "undefined" || !tokenExConfig) {
            return;
        }

        setIsTokenExIframeLoaded(false);
        setUpTokenEx();
    }, [tokenExConfig, typeof TokenEx]);

    useEffect(() => resetForm(), [paymentMethod]);

    useEffect(() => {
        if (isCardNumberTokenized) {
            const payload = formatPayload({
                paymentMethodDto,
                paymentMethod,
                saveCard,
                cardHolderName,
                cardNumber,
                cardType,
                expirationMonth,
                expirationYear,
                securityCode,
                address1,
                countryId,
                stateId,
                city,
                postalCode,
                accountNumber,
                accountType,
                bankName,
                firstName,
                lastName,
                nameOnAccount,
                routingNumber,
                phoneNumber,
            });
            setShowSpinner(true);
            onSubmit(payload);
            setIsCardNumberTokenized(false);
        }
    }, [isCardNumberTokenized]);

    useEffect(() => {
        if (typeof tokenName !== "undefined") {
            loadTokenExConfig({ token: tokenName, isECheck });
        }
    }, [paymentMethod]);

    useEffect(() => {
        handlePaymetricValidateSuccess(paymetricValidateSuccess && !showFormErrors);
    }, [paymetricValidateSuccess, showFormErrors]);

    if (showSpinner) {
        return (
            <StyledWrapper {...styles.loadingSpinnerWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    return (
        <StyledForm
            id="invoicePaymentSubmitForm"
            noValidate={true}
            onSubmit={handleFormSubmit}
            {...styles.wrapper}
            data-test-selector="invoicePaymentSubmitForm"
        >
            {/* This button should only be used to trigger the submit of the form. Should be on the top of the form. */}
            <button id="invoicePaymentSubmitForm-submit" type="submit" style={{ display: "none" }}>
                {translate("Submit")}
            </button>

            <StyledWrapper {...styles.wrapper}>
                <Link
                    onClick={() => {
                        setCurrentStep(2);
                    }}
                    {...styles.backLink}
                >
                    {translate("Change Selections")}
                </Link>

                <Typography variant="h4">{translate("Invoice Payment Details")}</Typography>
                <GridContainer {...styles.paymentContainer}>
                    <GridItem {...styles.leftGridItem}>
                        {
                            // paymentMethods && paymentMethods.length > 0 && !isBuyer1 &&
                            !isBuyer1 && (
                                <>
                                    {paymentMethodDto?.isPaymentProfile &&
                                        !paymentMethodDto.isPaymentProfileExpired && (
                                            <PaymentProfileBillingAddress address={paymentMethodDto.billingAddress} />
                                        )}
                                    {isCreditCard && !useAdyenDropIn && (
                                        <>
                                            <CreditCardDetailsEntry
                                                canSaveCard={false}
                                                iframe={iframeName}
                                                paymetricFrameRef={paymetricFrameRef}
                                                isTokenExIframeLoaded={isTokenExIframeLoaded}
                                                saveCard={saveCard}
                                                onSaveCardChange={handleSaveCardChange}
                                                cardHolderName={cardHolderName}
                                                cardHolderNameRef={cardHolderNameRef}
                                                onCardHolderNameChange={handleCardHolderNameChange}
                                                cardHolderNameError={showFormErrors ? cardHolderNameError : undefined}
                                                cardNumber={cardNumber}
                                                cardNumberRef={cardNumberRef}
                                                onCardNumberChange={handleCardNumberChange}
                                                cardNumberError={showFormErrors ? cardNumberError : undefined}
                                                cardType={cardType}
                                                cardTypeRef={cardTypeRef}
                                                possibleCardType={possibleCardType}
                                                onCardTypeChange={handleCardTypeChange}
                                                cardTypeError={showFormErrors ? cardTypeError : undefined}
                                                expirationMonth={expirationMonth}
                                                expirationMonthRef={expirationMonthRef}
                                                onExpirationMonthChange={handleExpirationMonthChange}
                                                expirationYear={expirationYear}
                                                expirationYearRef={expirationYearRef}
                                                onExpirationYearChange={handleExpirationYearChange}
                                                expirationError={showFormErrors ? expirationError : undefined}
                                                securityCode={securityCode}
                                                securityCodeRef={securityCodeRef}
                                                onSecurityCodeChange={handleSecurityCodeChange}
                                                securityCodeError={showFormErrors ? securityCodeError : undefined}
                                                availableCardTypes={paymentOptions.cardTypes ?? []}
                                                availableMonths={paymentOptions.expirationMonths ?? []}
                                                availableYears={paymentOptions.expirationYears ?? []}
                                                extendedStyles={styles.creditCardDetails}
                                                isIframeLoaded={isIframeLoaded}
                                                error={showFormErrors ? paystandError : undefined}
                                                {...({
                                                    cardNickName: "cardNickName",
                                                    onCardNickNameChange,
                                                    isInvoicePayment: true,
                                                    hideTitle: true,
                                                } as any)}
                                            />
                                        </>
                                    )}
                                    {paymentMethodDto?.isPaymentProfile && paymentMethodDto.isPaymentProfileExpired && (
                                        <StyledWrapper>
                                            <Typography>{siteMessage("Checkout_PaymentProfileExpired")}</Typography>
                                            {savedPaymentsPageLink && (
                                                <Link onClick={handleEditCardClick}>{translate("Edit Card")}</Link>
                                            )}
                                        </StyledWrapper>
                                    )}
                                    {paymentMethodDto?.isPaymentProfile &&
                                        !paymentMethodDto.isPaymentProfileExpired &&
                                        !bypassCvvForSavedCards && (
                                            <GridItem width={6}>
                                                {/* <SavedPaymentProfileEntry
                                                    iframe={iframeName}
                                                    isTokenExIframeLoaded={isTokenExIframeLoaded}
                                                    securityCode={securityCode}
                                                    onSecurityCodeChange={handleSecurityCodeChange}
                                                    securityCodeError={showFormErrors ? securityCodeError : undefined}
                                                /> */}
                                            </GridItem>
                                        )}
                                </>
                            )
                        }
                    </GridItem>
                    <GridItem {...styles.rightGridItem}>
                        {
                            // paymentMethodDto?.isCreditCard && !useAdyenDropIn && (
                            <CreditCardBillingAddressEntry
                                useBillTo={useBillingAddress}
                                onUseBillToChange={handleUseBillingAddressChange}
                                billTo={billToState.value}
                                address1={address1}
                                address1Ref={address1Ref}
                                onAddress1Change={handleAddressChange}
                                address1Error={showFormErrors ? address1Error : undefined}
                                country={countryId}
                                onCountryChange={handleCountryChange}
                                countryError={showFormErrors ? countryError : undefined}
                                state={stateId}
                                stateRef={stateRef}
                                onStateChange={handleStateChange}
                                stateError={showFormErrors ? stateError : undefined}
                                city={city}
                                cityRef={cityRef}
                                onCityChange={handleCityChange}
                                cityError={showFormErrors ? cityError : undefined}
                                postalCode={postalCode}
                                postalCodeRef={postalCodeRef}
                                onPostalCodeChange={handlePostalCodeChange}
                                postalCodeError={showFormErrors ? postalCodeError : undefined}
                                availableCountries={countries ?? []}
                                availableStates={selectedCountry?.states}
                                extendedStyles={styles.creditCardAddress}
                                {...({
                                    cardAddress2,
                                    setCardAddress2Change,
                                    isInvoicePayment: true,
                                } as any)}
                            />
                            // )
                        }
                    </GridItem>
                </GridContainer>
            </StyledWrapper>
        </StyledForm>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(InvoicePaymentDetails)));
